import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {GooglePay} from '@bambora/wallet';
import {ChargeItem} from '../../models/Order';
import {BamboraGooglePayWalletSession} from '../../models/PaymentModels';
import {AuthorizedApiService} from '../../services/authorized-api.service';
import {firstValueFrom} from 'rxjs';
import MoneyUtils, {ErrorUtils} from '../../utils/Utils';
import {environment} from '../../../environments/environment';
import {WalletButtonState} from './apple-pay-button.component';

const HEYNOW_GOOGLE_PAY_MERCHANT_ID = 'BCR2DN4T2PNNHSZE';

@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'app-google-pay-button',
  // Custom button (not Google's createButton) to avoid the SDK's two-state render morph.
  // GooglePay.create()/isReady() still gate visibility; click delegates to googlePay.start().
  template: `
    <button *ngIf="state === 'ready'"
            type="button"
            (click)="startPayment()"
            class="w-full h-14 rounded-2xl bg-black flex items-center justify-center border-2 border-black active:opacity-80 transition-opacity">
      <svg class="h-7 w-auto relative top-[2px]" viewBox="0 0 41 18" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fill-rule="evenodd">
          <path d="M19.526 2.635v4.083h2.518c.6 0 1.096-.202 1.488-.605.403-.402.605-.882.605-1.437 0-.544-.202-1.018-.605-1.422-.392-.413-.888-.62-1.488-.62h-2.518zm0 5.52v4.736h-1.504V1.198h3.99c1.013 0 1.873.337 2.582 1.012.72.675 1.08 1.497 1.08 2.466 0 .991-.36 1.819-1.08 2.482-.697.665-1.559.996-2.583.996h-2.485v.001zm7.668 2.287c0 .392.166.718.499.98.332.26.722.391 1.168.391.633 0 1.196-.234 1.692-.701.497-.469.744-1.019.744-1.65-.469-.37-1.123-.555-1.962-.555-.61 0-1.12.148-1.528.442-.409.294-.613.657-.613 1.093m1.946-5.815c1.112 0 1.989.297 2.633.89.642.594.964 1.408.964 2.442v4.932h-1.439v-1.11h-.065c-.622.914-1.45 1.372-2.486 1.372-.882 0-1.621-.262-2.215-.784-.594-.523-.891-1.176-.891-1.96 0-.828.313-1.486.94-1.976s1.463-.735 2.51-.735c.892 0 1.629.163 2.206.49v-.344c0-.522-.207-.966-.621-1.33a2.132 2.132 0 0 0-1.455-.547c-.84 0-1.504.353-1.995 1.062l-1.324-.834c.73-1.045 1.81-1.568 3.238-1.568m11.853.262l-5.02 11.53H34.42l1.864-4.034-3.302-7.496h1.635l2.387 5.749h.032l2.322-5.75z" fill="#ffffff"/>
          <path d="M13.448 7.134c0-.473-.04-.93-.116-1.366H6.988v2.588h3.634a3.11 3.11 0 0 1-1.344 2.042v1.68h2.169c1.27-1.17 2.001-2.9 2.001-4.944" fill="#4285F4"/>
          <path d="M6.988 13.7c1.816 0 3.344-.595 4.459-1.621l-2.169-1.681c-.603.406-1.38.643-2.29.643-1.754 0-3.244-1.182-3.776-2.774H.978v1.731a6.728 6.728 0 0 0 6.01 3.703" fill="#34A853"/>
          <path d="M3.212 8.267a4.034 4.034 0 0 1 0-2.572V3.964H.978A6.678 6.678 0 0 0 .261 6.98c0 1.085.26 2.11.717 3.017l2.234-1.731z" fill="#FABB05"/>
          <path d="M6.988 2.921c.992 0 1.88.34 2.58 1.008v.001l1.92-1.918C10.324.928 8.804.262 6.989.262a6.728 6.728 0 0 0-6.01 3.702l2.234 1.731c.532-1.592 2.022-2.774 3.776-2.774" fill="#E94235"/>
        </g>
      </svg>
    </button>
  `,
  styles: [`:host { display: block; }`]
})
export class GooglePayButtonComponent implements AfterViewInit, OnChanges {
  @Input() totalSek!: number;
  @Input() tipParam!: string;
  @Input() payableRefs?: string;
  @Input() chargeItems?: ChargeItem[];
  @Input() plKey?: string;
  @Input() receiptKey?: string;
  @Input() itemRefs?: any[];
  @Input() bamboraMerchantNumber!: string;
  @Input() declineRoute?: string;

  @Output() paymentComplete = new EventEmitter<{ receiptKey: string }>();
  @Output() paymentFailed = new EventEmitter<any>();
  @Output() stateChange = new EventEmitter<WalletButtonState>();
  @Output() processingChange = new EventEmitter<boolean>();

  state: WalletButtonState = 'pending';
  private googlePay?: GooglePay;
  private lastReceiptKey?: string;
  private setupGen = 0;
  private viewReady = false;

  constructor(private api: AuthorizedApiService, private cdr: ChangeDetectorRef) {}

  async ngAfterViewInit(): Promise<void> {
    this.viewReady = true;
    await this.setupWallet();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.viewReady && changes['totalSek'] && !changes['totalSek'].firstChange) {
      await this.setupWallet();
    }
  }

  private setState(s: WalletButtonState) {
    if (this.state !== s) {
      this.state = s;
      this.stateChange.emit(s);
      this.cdr.detectChanges();
    }
  }

  private async setupWallet(): Promise<void> {
    if (!(this.totalSek > 0)) return;
    const gen = ++this.setupGen;
    try {
      const googlePay = await GooglePay.create(
        {
          clientConfiguration: { environment: environment.production ? 'PRODUCTION' : 'TEST' },
          sessionProvider: { fetchSession: () => this.fetchSession() },
        },
        {
          merchantInfo: { merchantName: 'Heynow', merchantId: HEYNOW_GOOGLE_PAY_MERCHANT_ID },
          allowedPaymentMethods: [{
            parameters: { allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX'] },
            tokenizationSpecification: { parameters: { gatewayMerchantId: this.bamboraMerchantNumber } },
          }],
          transactionInfo: { totalPrice: this.totalSek.toFixed(2), currencyCode: MoneyUtils.currency(), countryCode: MoneyUtils.country() },
        },
      );
      if (gen !== this.setupGen) return;

      const ready = await googlePay.isReady();
      if (gen !== this.setupGen) return;
      console.log('[GooglePay] isReady()', ready, 'env=', environment.production ? 'PRODUCTION' : 'TEST', 'totalSek=', this.totalSek);

      if (!ready) {
        this.googlePay = undefined;
        this.setState('unavailable');
        return;
      }

      this.googlePay = googlePay;
      this.setState('ready');
    } catch (err) {
      if (gen !== this.setupGen) return;
      console.warn('Google Pay init failed', err);
      this.setState('unavailable');
    }
  }

  private async fetchSession() {
    const session = await firstValueFrom(
      this.api.createBamboraGooglePayWalletSession(this.tipParam, this.receiptKey, this.itemRefs, this.plKey, this.payableRefs, this.chargeItems, this.declineRoute),
    );
    this.lastReceiptKey = session.receipt_key;
    return { data: session as BamboraGooglePayWalletSession };
  }

  async startPayment() {
    if (!this.googlePay) return;
    // The OS sheet closes before start() resolves (backend authorize is still running) — keep a
    // spinner up for that gap. We leave it on through success since navigation unmounts us.
    this.processingChange.emit(true);
    try {
      await this.googlePay.start();
      if (this.lastReceiptKey) {
        this.paymentComplete.emit({ receiptKey: this.lastReceiptKey });
      } else {
        this.processingChange.emit(false);
        this.paymentFailed.emit({});
      }
    } catch (err) {
      this.processingChange.emit(false);
      this.paymentFailed.emit(ErrorUtils.isUserCancel(err) ? { message: 'Betalningen avbruten' } : err);
    }
  }
}
